import { useCallback, useState } from "react";

import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { CardCvcChangeEvent } from "@shared/modules/payments/common/CardCvcInput/CardCvcInput.props";
// eslint-disable-next-line import/no-cycle
import AuthorizeElement from "../AuthorizeElement/AuthorizeElement.web";

export type AuthorizeCardCvcElementProps = {
  /**
   * Fires when a value is well-formed and potentially complete.
   * It is not an indicator of whether a customer is done with their input --
   * it only indicates that the Element contains a potentially complete, well-formed value.
   */
  onValidField?: () => void;
  /**
   * Fires when a value is deemed invalid
   */
  onInvalidField?: () => void;
};

/**
 * A containing element for a an AuthorizeElement component configured as a cardCvc element.
 */
function AuthorizeCardCvcElement({
  onValidField,
  onInvalidField,
}: AuthorizeCardCvcElementProps) {
  const { t } = useTranslation("checkout");
  const [error, setError] = useState("");

  const handleChange = useCallback(
    (evt: CardCvcChangeEvent) => {
      // Event handling is unique per element
      if (evt.error) {
        setError(evt.error.message);
      } else if (!evt.error) {
        setError("");
      }

      if (evt.complete) {
        onValidField?.();
      } else {
        onInvalidField?.();
      }
    },
    [onValidField, onInvalidField]
  );

  return (
    <Box>
      <Box>
        <Text fontWeight={700} mb="2xs" fontSize="0.875rem">
          {t("CVC")}
        </Text>
      </Box>
      <Box
        border="1px solid"
        borderColor={!error ? "border" : "red.600"}
        borderRadius="sm"
        padding="0px 12px"
        lineHeight="1.2em"
        bg="bg"
      >
        <AuthorizeElement element="cardCvc" onChange={handleChange} />
      </Box>
      <Box>
        <Text
          color="red.600"
          noOfLines={3}
          data-testid="authorize-element-CardCvc-error"
        >
          {error}
        </Text>
      </Box>
    </Box>
  );
}

export default AuthorizeCardCvcElement;
